import { useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import {
  IconGift,
  IconHistory,
  IconHome,
  IconHomeFilled,
  IconMapPin,
  IconMapPinFilled,
  IconRubberStamp,
} from '@tabler/icons-react'
import { useRouter } from 'next/router'
import styles from './BottomNavigation.module.css'
import { Typography } from './Typography'

export type BottomNavItem = 'home' | 'spot' | 'stamp' | 'reward' | 'mypage'

export type BottomNavigationProps = {
  /** 現在アクティブなナビアイテム */
  activeItem?: BottomNavItem
  /** ナビアイテムクリック時のコールバック */
  onItemClick?: (item: BottomNavItem) => void
  /** GETボタン表示（デフォルト: true） */
  showStamp?: boolean
  /** GETボタン押下時のコールバック。指定がない場合は何もしない */
  onApplicationTriggerClick?: () => void
  /** 追加CSSクラス（position等のスタイル上書きに使用） */
  className?: string
}

const ACTIVE_COLOR = 'var(--mantine-color-primary-5)'
const INACTIVE_COLOR = '#cbcbce'

function HomeIcon({ active }: { active: boolean }) {
  return active ? (
    <IconHomeFilled size={24} color={ACTIVE_COLOR} aria-hidden="true" />
  ) : (
    <IconHome size={24} stroke={1.5} color={INACTIVE_COLOR} aria-hidden="true" />
  )
}

function SpotIcon({ active }: { active: boolean }) {
  return active ? (
    <IconMapPinFilled size={24} color={ACTIVE_COLOR} aria-hidden="true" />
  ) : (
    <IconMapPin size={24} stroke={1.5} color={INACTIVE_COLOR} aria-hidden="true" />
  )
}

function RewardIcon({ active }: { active: boolean }) {
  return <IconGift size={24} stroke={1.5} color={active ? ACTIVE_COLOR : INACTIVE_COLOR} aria-hidden="true" />
}

function MyPageIcon({ active }: { active: boolean }) {
  return <IconHistory size={24} stroke={1.5} color={active ? ACTIVE_COLOR : INACTIVE_COLOR} aria-hidden="true" />
}

function StampIcon() {
  return <IconRubberStamp size={28} stroke={1.5} color="var(--sub-color)" aria-hidden="true" />
}

export function BottomNavigation({
  activeItem = 'home',
  onItemClick,
  showStamp = true,
  onApplicationTriggerClick,
  className,
}: BottomNavigationProps) {
  const { gen } = useCampaignSlug()
  const router = useRouter()

  const items: { key: BottomNavItem; label: string; path: string }[] = showStamp
    ? [
        { key: 'home', label: 'ホーム', path: gen('/') },
        { key: 'spot', label: 'スポット', path: gen('/spots') },
        { key: 'stamp', label: 'GET', path: '' },
        { key: 'reward', label: '特典', path: gen('/rewards') },
        { key: 'mypage', label: '履歴', path: gen('/mypage') },
      ]
    : [
        { key: 'home', label: 'ホーム', path: gen('/') },
        { key: 'spot', label: 'スポット', path: gen('/spots') },
        { key: 'reward', label: '特典', path: gen('/rewards') },
        { key: 'mypage', label: '履歴', path: gen('/mypage') },
      ]

  return (
    <nav className={`${styles.root} ${className ?? ''}`} aria-label="ボトムナビゲーション">
      {items.map((item) => {
        const isActive = activeItem === item.key

        if (item.key === 'stamp') {
          return (
            <button
              key={item.key}
              type="button"
              className={styles.stampButton}
              onClick={() => {
                onApplicationTriggerClick?.()
              }}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
            >
              <div className={styles.stampCircle}>
                <StampIcon />
              </div>
              <Typography
                variant="caption2"
                component="span"
                className={styles.stampLabel}
                style={{ fontWeight: 700, fontSize: 12 }}
              >
                {item.label}
              </Typography>
            </button>
          )
        }

        return (
          <button
            key={item.key}
            type="button"
            className={styles.item}
            onClick={() => {
              router.push(item.path)
            }}
            aria-current={isActive ? 'page' : undefined}
          >
            {item.key === 'home' && <HomeIcon active={isActive} />}
            {item.key === 'spot' && <SpotIcon active={isActive} />}
            {item.key === 'reward' && <RewardIcon active={isActive} />}
            {item.key === 'mypage' && <MyPageIcon active={isActive} />}
            <Typography
              variant="caption2"
              component="span"
              className={`${styles.itemLabel} ${isActive ? styles.itemLabelActive : ''}`}
              style={{ fontWeight: 700, fontSize: 12 }}
            >
              {item.label}
            </Typography>
          </button>
        )
      })}
    </nav>
  )
}
